import { NBAGame, GameStatus, QuarterScore, GameStatistic, GameTimelineEvent } from '@/types';

const BASE_URL = 'https://site.api.espn.com/apis/site/v2/sports/basketball/nba';

const cache = new Map<string, { data: unknown; ts: number }>();

function cached<T>(key: string, ttl: number, fetcher: () => Promise<T>): Promise<T> {
  const entry = cache.get(key);
  if (entry && Date.now() - entry.ts < ttl) return Promise.resolve(entry.data as T);
  return fetcher().then((data) => {
    cache.set(key, { data, ts: Date.now() });
    return data;
  });
}

function mapStatus(event: any): GameStatus {
  const state = event.status?.type?.state ?? '';
  const name = event.status?.type?.name ?? '';
  if (state === 'post' || event.status?.type?.completed) return 'finished';
  if (state === 'in') {
    if (name === 'STATUS_HALFTIME') return 'halftime';
    return 'live';
  }
  return 'scheduled';
}

const STAT_LABELS: Record<string, string> = {
  fieldGoalPct: 'Aproveitamento FG',
  threePointFieldGoalPct: 'Bolas de 3',
  freeThrowPct: 'Lances livres',
  totalRebounds: 'Rebotes',
  assists: 'Assistências',
  steals: 'Roubos',
  blocks: 'Tocos',
  turnovers: 'Erros',
  fouls: 'Faltas',
  pointsInPaint: 'Pontos no garrafão',
  fastBreakPoints: 'Contra-ataque',
  largestLead: 'Maior vantagem',
};

const STAT_ORDER = [
  'fieldGoalPct',
  'threePointFieldGoalPct',
  'freeThrowPct',
  'totalRebounds',
  'assists',
  'steals',
  'blocks',
  'turnovers',
  'fouls',
  'pointsInPaint',
  'fastBreakPoints',
  'largestLead',
];

function statNumber(value: string): number | undefined {
  const clean = String(value).replace('%', '').replace(',', '.');
  const parsed = Number.parseFloat(clean);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function mapTeamStats(summary: any): GameStatistic[] | undefined {
  const teams: any[] = summary?.boxscore?.teams ?? [];
  const home = teams.find((t) => t.homeAway === 'home');
  const away = teams.find((t) => t.homeAway === 'away');
  if (!home || !away) return undefined;

  const homeStats = new Map((home.statistics ?? []).map((s: any) => [s.name, s.displayValue]));
  const awayStats = new Map((away.statistics ?? []).map((s: any) => [s.name, s.displayValue]));

  const rows: GameStatistic[] = [];
  STAT_ORDER.forEach((name) => {
    const homeValue = homeStats.get(name);
    const awayValue = awayStats.get(name);
    if (homeValue == null && awayValue == null) return;
    rows.push({
      id: name,
      label: STAT_LABELS[name] ?? name,
      home: String(homeValue ?? '-'),
      away: String(awayValue ?? '-'),
      homeValue: statNumber(String(homeValue ?? '')),
      awayValue: statNumber(String(awayValue ?? '')),
    });
  });

  return rows.length > 0 ? rows : undefined;
}

function mapScoringPlays(summary: any, homeId: string, awayId: string): GameTimelineEvent[] | undefined {
  const plays: any[] = summary?.plays ?? [];
  const scoring = plays
    .filter((play) => play.scoringPlay)
    .slice(-30)
    .map((play): GameTimelineEvent => {
      const teamId = String(play.team?.id ?? '');
      const scoreValue = Number(play.scoreValue ?? play.pointsAttempted ?? 0);
      const period = play.period?.displayValue ?? (play.period?.number ? `${play.period.number}º quarto` : undefined);
      return {
        id: String(play.id),
        side: teamId === homeId ? 'home' : teamId === awayId ? 'away' : 'center',
        minute: play.clock?.displayValue,
        period,
        type: 'score',
        title: scoreValue > 0 ? `+${scoreValue} pontos` : 'Pontuação',
        detail: play.text,
        score: {
          home: Number(play.homeScore ?? 0),
          away: Number(play.awayScore ?? 0),
        },
      };
    });

  if (scoring.length === 0) return undefined;

  const periods = new Set(scoring.map((play) => play.period).filter(Boolean));
  const markers: GameTimelineEvent[] = Array.from(periods).map((period, index) => ({
    id: `period-${index}-${period}`,
    side: 'center',
    period,
    type: 'period_start',
    title: `Início do ${period}`,
  }));

  return [...markers, ...scoring].sort((a, b) => String(a.period ?? '').localeCompare(String(b.period ?? '')));
}

function mapGame(event: any, summary?: any): NBAGame {
  const comp = event.competitions?.[0] ?? event;
  const competitors = comp.competitors ?? event.competitors ?? [];
  const home = competitors.find((c: any) => c.homeAway === 'home');
  const away = competitors.find((c: any) => c.homeAway === 'away');

  const homeLines: any[] = home?.linescores ?? [];
  const awayLines: any[] = away?.linescores ?? [];
  const qt: QuarterScore[] = homeLines.map((ls: any, i: number) => ({
    period: i + 1,
    home: parseInt(ls.value ?? '0', 10),
    away: parseInt(awayLines[i]?.value ?? '0', 10),
  }));

  const status = event.status ?? comp.status;
  const homeId = String(home?.team?.id ?? '');
  const awayId = String(away?.team?.id ?? '');

  return {
    id: String(event.id),
    sport: 'nba',
    homeTeam: {
      id: homeId,
      name: home?.team?.displayName ?? home?.team?.name ?? '',
      shortName: home?.team?.abbreviation ?? '',
      logo: home?.team?.logo ?? '',
    },
    awayTeam: {
      id: awayId,
      name: away?.team?.displayName ?? away?.team?.name ?? '',
      shortName: away?.team?.abbreviation ?? '',
      logo: away?.team?.logo ?? '',
    },
    score: {
      home: parseInt(home?.score ?? '0', 10),
      away: parseInt(away?.score ?? '0', 10),
    },
    status: mapStatus(event),
    period: status?.period ?? 0,
    clock: status?.displayClock !== '0:00' ? status?.displayClock : undefined,
    startTime: event.date ?? comp.startDate ?? '',
    quarter_scores: qt.length > 0 ? qt : undefined,
    timeline: summary ? mapScoringPlays(summary, homeId, awayId) : undefined,
    statistics: summary ? mapTeamStats(summary) : undefined,
  };
}

export async function getNBATodayGames(): Promise<NBAGame[]> {
  const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  return cached(`espn:nba:today:${today}`, 30_000, async () => {
    const res = await fetch(`${BASE_URL}/scoreboard?dates=${today}`);
    if (!res.ok) throw new Error(`ESPN NBA HTTP ${res.status}`);
    const json = await res.json();
    const events: any[] = json.events ?? [];
    console.log(`[ESPN NBA] Jogos hoje: ${events.length}`);
    return events.map((event) => mapGame(event));
  });
}

export async function getNBALiveGames(): Promise<NBAGame[]> {
  const games = await getNBATodayGames();
  return games.filter((g) => g.status === 'live');
}

export async function getNBAGameDetail(gameId: string): Promise<NBAGame | null> {
  return cached(`espn:nba:game:${gameId}`, 30_000, async () => {
    const res = await fetch(`${BASE_URL}/summary?event=${gameId}`);
    if (!res.ok) return null;
    const json = await res.json();
    const event = json.header?.competitions?.[0];
    if (!event) return null;
    return mapGame({ ...json.header, competitions: [event] }, json);
  });
}
